<template>
    <div>
        <div>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-6">
                <!-- Card Number -->
                <div class="md:col-span-4">
                    <div>{{ t('cardNumber') }}</div>
                    <div
                        :id="FIELD_IDS.cardNumber"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>

                <!-- Expiration Date -->
                <div class="md:col-span-1">
                    <div>{{ t('expirationDate') }}</div>
                    <div
                        :id="FIELD_IDS.expiry"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>

                <!-- CVV -->
                <div class="md:col-span-1">
                    <div>{{ t('securityCode') }}</div>
                    <div
                        :id="FIELD_IDS.cvv"
                        class="h-[36px] rounded-md border border-gray-300 p-2"
                    />
                </div>
            </div>

            <!-- Windcave expects this element even if you don't require it -->
            <div class="mb-8 hidden">
                <div>{{ t('cardholderName') }}</div>
                <div
                    :id="FIELD_IDS.cardholder"
                    class="h-[36px] rounded-md border border-gray-300 p-2"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { type TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import type { HostedFieldsController } from '~/components/Payments/windcave-types'
import { useShowAlert } from '#imports'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    paymentSourceId: string
    paymentSourceData: {
        url: string
        supportedCards: string[]
        sessionId: string
    }
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'transactionError', payload: string): void
    (e: 'validationError', payload: string): void
}>()

const FIELD_IDS = Object.freeze({
    cardNumber: 'wc-card-number',
    expiry: 'wc-expiry',
    cvv: 'wc-cvv',
    cardholder: 'wc-cardholder-name'
})

const { t } = useI18n({ useScope: 'global' })
const showAlert = useShowAlert()

let controller: HostedFieldsController | null = null
const { incrementProcessing, decrementProcessing } = useCheckoutFormProcessing()

async function submitPaymentMethod() {
    if (controller === null) {
        return
    }

    if (props.paymentSourceData === undefined) {
        return
    }

    const windcaveData = props.paymentSourceData

    incrementProcessing()

    try {
        const valid = await new Promise((resolve, reject) => {
            if (controller === null) {
                reject()
                return
            }

            controller.validateField(
                null,
                result => {
                    try {
                        const allValid = Object.values(result || {}).every(
                            field => field?.isValidationPass === true
                        )
                        resolve(allValid)
                    } catch (error) {
                        reject(error)
                    }
                },
                reject
            )
        })

        if (!valid) {
            emit(
                'validationError',
                'Please check your card details and try again'
            )
            return
        }

        await new Promise<void>((resolve, reject) => {
            if (controller === null) {
                reject()
                return
            }

            controller.submit(
                windcaveData.url,
                60,
                async (status: string) => {
                    if (status === '3DSecure') {
                        return
                    }

                    if (status !== 'done') {
                        emit(
                            'transactionError',
                            'Transaction failed, please check your details and try again (10)'
                        )
                        return resolve()
                    }

                    try {
                        emit('paymentMethod', {
                            type: 'TokenPaymentMethod',
                            token: 'WC|' + windcaveData.sessionId,
                            isCard: true,
                            paymentTodayAllowed: true,
                            brand: '',
                            lastFour: null,
                            accountExpirationDate: null
                        })

                        resolve()
                    } catch (e) {
                        reject(e)
                    }
                },
                error => {
                    emit(
                        'transactionError',
                        'Transaction failed, please check your details and try again (40)'
                    )
                    reject(error)
                }
            )
        })
    } catch {
        emit(
            'transactionError',
            'There was a problem processing your payment (50)'
        )
    } finally {
        decrementProcessing()
    }
}

onMounted(async () => {
    // @ts-expect-error: Windcave does not have types
    const HostedFields = window.WindcavePayments?.HostedFields
    if (!HostedFields) {
        await showAlert(
            'Heads Up!',
            'Windcave: Hosted Fields library not available.'
        )
        return
    }

    controller = HostedFields.create(
        {
            env: 'sec',
            fields: {
                CardNumber: {
                    container: FIELD_IDS.cardNumber,
                    tabOrder: 1,
                    placeholder: '1234 1234 1234 1234',
                    supportedCards: [...props.paymentSourceData.supportedCards]
                },
                ExpirationDate: {
                    container: FIELD_IDS.expiry,
                    tabOrder: 2,
                    placeholder: 'MM/YY'
                },
                CVV: {
                    container: FIELD_IDS.cvv,
                    tabOrder: 3,
                    placeholder: 'CVV'
                }
            },
            styles: {
                'input-invalid': { color: 'red' }
            }
        },
        30,
        () => {
            decrementProcessing()
        },
        (errMsg: unknown) => {
            console.error('Hosted Fields init error', errMsg)
            decrementProcessing()
            showAlert('Heads Up!', 'Windcave: Hosted Fields init error')
        }
    )
})

defineExpose({
    submitPaymentMethod
})
</script>
